import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Shield, Zap, Clock, Play, Pause, Volume2, VolumeX,
  CheckCircle, ChevronRight, ChevronLeft, X, Calculator, FileText,
  Truck, Building2, Shovel, HardHat, Leaf, BarChart3, Loader2,
  DollarSign, ClipboardList, Settings, Download, Mail, Database,
  Users, MapPin, Star, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import MassMasterLogo from '../components/MassMasterLogo';

// Professional demo slides with narration scripts - Updated with all features
const DEMO_SLIDES = [
  {
    id: 'slide_welcome',
    title: 'Mass Master Pro',
    subtitle: 'Professional Construction Estimating',
    narration: "Welcome to Mass Master Pro. This is the complete estimating platform built by contractors, for contractors. We've got specialized tools covering everything from mobilization to AI-powered document analysis. Add your own materials, manage your subs, and generate professional bids. Let's take a quick tour.",
    features: [
      { icon: HardHat, text: 'Specialized calculators' },
      { icon: ClipboardList, text: 'Organized by job phase' },
      { icon: Database, text: 'Cost databases' },
      { icon: Zap, text: 'AI-powered features' }
    ],
    bgGradient: 'from-slate-900 via-blue-900 to-slate-900',
    accentColor: 'blue',
    audioDuration: 15000
  },
  {
    id: 'slide_mobilization',
    title: 'Mobilization & Demobilization',
    subtitle: 'Project Setup Made Simple',
    narration: "Every job starts with mobilization and demobilization. This tab tracks all your setup costs. Equipment transport, lowboy rentals, crew mobilization, permits, and any other startup expenses. Each item shows as a separate line in your estimate with full fuel cost tracking on equipment.",
    features: [
      { icon: Truck, text: 'Equipment transport' },
      { icon: Users, text: 'Crew mobilization' },
      { icon: DollarSign, text: 'Fuel cost tracking' },
      { icon: FileText, text: 'Other startup costs' }
    ],
    bgGradient: 'from-slate-900 via-purple-900 to-slate-900',
    accentColor: 'purple',
    audioDuration: 12000
  },
  {
    id: 'slide_excavator',
    title: 'Excavation & Demolition',
    subtitle: 'Earthwork Made Simple',
    narration: "The Demolition tab handles material weights for disposal. Excavation calculates cubic yards with swell factors built in. Both include fuel consumption tracking on equipment. Task duration from each tab feeds directly into your project schedule.",
    features: [
      { icon: Shovel, text: 'Cubic yard calculations' },
      { icon: Calculator, text: 'Auto swell factors' },
      { icon: Truck, text: 'Trucking integration' },
      { icon: Clock, text: 'Task duration tracking' }
    ],
    bgGradient: 'from-slate-900 via-amber-900 to-slate-900',
    accentColor: 'amber',
    audioDuration: 12000
  },
  {
    id: 'slide_backfill',
    title: 'Import & Backfill',
    subtitle: 'Material Placement Tracking',
    narration: "Import tracks material coming in. Gravel, sand, soil, with material and trucking costs per ton. Backfill adds compaction factors for placed material. Both calculators give you tonnage and total cost ready for your estimate.",
    features: [
      { icon: Truck, text: 'Material & trucking costs' },
      { icon: Building2, text: 'Compaction factors' },
      { icon: BarChart3, text: 'Tonnage calculations' },
      { icon: DollarSign, text: 'Cost per ton tracking' }
    ],
    bgGradient: 'from-slate-900 via-teal-900 to-slate-900',
    accentColor: 'teal',
    audioDuration: 10000
  },
  {
    id: 'slide_civil',
    title: 'Civil & Environmental',
    subtitle: 'Roads, Drainage & Compliance',
    narration: "Civil handles your road work. Paving specs, storm drainage, utility trenches, curb and gutter. Environmental covers erosion control and stormwater management. Both tabs include labor, equipment with fuel tracking, and task duration.",
    features: [
      { icon: Building2, text: 'Paving & drainage' },
      { icon: Leaf, text: 'Environmental compliance' },
      { icon: Shield, text: 'Stormwater management' },
      { icon: Clock, text: 'Task scheduling' }
    ],
    bgGradient: 'from-slate-900 via-green-900 to-slate-900',
    accentColor: 'green',
    audioDuration: 11000
  },
  {
    id: 'slide_estimate',
    title: 'Project Estimates',
    subtitle: 'Professional Bid Documents',
    narration: "This is where it all comes together. The Estimate tab pulls in every line item from every calculator. Mobilization and demobilization costs, materials, labor, equipment with fuel. Add markup, contingency, waste, and taxes. Hide any adjustments from the client while still including them in your price. Six professional templates to choose from.",
    features: [
      { icon: ClipboardList, text: 'All line items combined' },
      { icon: DollarSign, text: 'Hide adjustments option' },
      { icon: Download, text: 'PDF & Excel export' },
      { icon: Mail, text: 'Email to clients' }
    ],
    bgGradient: 'from-slate-900 via-orange-900 to-slate-900',
    accentColor: 'orange',
    audioDuration: 14000
  },
  {
    id: 'slide_ai_docs',
    title: 'AI Document Analyzer',
    subtitle: 'Smart Document Intelligence',
    narration: "This is where AI meets construction. Upload blueprints, specs, contracts, or any project document. Our AI analyzes it instantly, extracts key details, materials, costs, and deadlines. Then chat with your documents to ask questions and get answers.",
    features: [
      { icon: FileText, text: 'Upload any document' },
      { icon: Zap, text: 'AI-powered analysis' },
      { icon: BarChart3, text: 'Extract key data' },
      { icon: Clock, text: 'Chat with documents' }
    ],
    bgGradient: 'from-slate-900 via-rose-900 to-slate-900',
    accentColor: 'rose',
    audioDuration: 11000
  },
  {
    id: 'slide_getstarted',
    title: 'Get Started Today',
    subtitle: 'Everything You Need, One Platform',
    narration: "That's Mass Master Pro. Mobilization and demobilization tracking, specialized calculators with fuel costs, regional cost data, contractor management, AI document analysis, and professional bid documents. Clear All buttons reset any tab instantly. Works on any device. Start estimating smarter, not harder.",
    features: [
      { icon: Zap, text: 'No setup required' },
      { icon: Calculator, text: 'Full calculator suite' },
      { icon: FileText, text: 'AI document tools' },
      { icon: Clock, text: 'Minutes, not hours' }
    ],
    bgGradient: 'from-slate-900 via-cyan-900 to-slate-900',
    accentColor: 'cyan',
    audioDuration: 12000,
    isFinal: true
  }
];

// Add Wrench icon for Pro Tools (defined before FEATURE_TABS to avoid initialization error)
const Wrench = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
  </svg>
);

// Feature tabs for the main section - Updated with all features
const FEATURE_TABS = [
  {
    name: 'Calculators',
    icon: Calculator,
    description: 'Specialized construction calculators',
    features: ['Demo & Excavation', 'Civil & Environmental', 'Trucking & Import']
  },
  {
    name: 'RSMeans Data',
    icon: Database,
    description: 'Regional construction cost database',
    features: ['8 US regions', 'Material costs', 'Labor & equipment rates']
  },
  {
    name: 'Contractors',
    icon: Users,
    description: 'Manage subs and contacts',
    features: ['License & insurance', 'Certifications', 'Rating system']
  },
  {
    name: 'AI Docs',
    icon: FileText,
    description: 'AI-powered document analysis',
    features: ['Upload blueprints & specs', 'Smart data extraction', 'Chat with documents']
  },
  {
    name: 'Estimates',
    icon: ClipboardList,
    description: 'Professional bid documents',
    features: ['PDF & Word export', 'Email to clients', 'Custom markup']
  },
  {
    name: 'Pro Tools',
    icon: Wrench,
    description: 'Engineering calculators',
    features: ['Slope & grade', 'Volume calcs', 'Unit conversions']
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  const [audioCache, setAudioCache] = useState({});
  const [audioError, setAudioError] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [demoCompleted, setDemoCompleted] = useState(false); // Track if demo finished
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  // Load audio for a slide - using ref to avoid dependency issues
  const audioCacheRef = useRef({});
  
  // Keep ref in sync with state
  useEffect(() => {
    audioCacheRef.current = audioCache;
  }, [audioCache]);

  // Preload ALL audio when demo opens - must complete before playing
  useEffect(() => {
    if (showDemo && !audioReady && Object.keys(audioCache).length < DEMO_SLIDES.length) {
      setAudioLoading(true);
      setAudioError(null);
      setLoadingProgress(0);
      
      const preloadAll = async () => {
        const cache = {};
        try {
          // Load all audio sequentially to show progress
          for (let i = 0; i < DEMO_SLIDES.length; i++) {
            const slideId = DEMO_SLIDES[i].id;
            
            // Skip if already cached
            if (audioCacheRef.current[slideId]) {
              cache[slideId] = audioCacheRef.current[slideId];
              setLoadingProgress(Math.round(((i + 1) / DEMO_SLIDES.length) * 100));
              continue;
            }
            
            try {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/generate-narration`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ script_id: slideId, voice: 'onyx', speed: 0.95 })
              });
              
              if (response.ok) {
                const data = await response.json();
                cache[slideId] = data.audio_base64;
                // Update loading progress
                setLoadingProgress(Math.round(((i + 1) / DEMO_SLIDES.length) * 100));
              }
            } catch (error) {
              console.error('Failed to load audio for slide:', slideId, error);
            }
          }
          
          // Update cache all at once
          setAudioCache(prev => ({ ...prev, ...cache }));
          setAudioReady(true);
        } catch (error) {
          console.error('Preload failed:', error);
          setAudioError('Audio narration unavailable');
          setAudioReady(true); // Allow demo to proceed even without audio
        }
        setAudioLoading(false);
      };
      
      preloadAll();
    }
  }, [showDemo, audioReady]); // Removed audioCache and loadAudio from dependencies

  // Auto-start playing ONLY after audio is ready and demo hasn't completed
  useEffect(() => {
    if (showDemo && audioReady && !isPlaying && !demoCompleted && Object.keys(audioCache).length === DEMO_SLIDES.length) {
      setIsPlaying(true);
    }
  }, [showDemo, audioReady, isPlaying, audioCache, demoCompleted]);

  // Play audio for current slide
  useEffect(() => {
    if (!isPlaying || !showDemo || !audioReady) return;
    
    // Clear any existing progress interval
    if (progressRef.current) {
      clearInterval(progressRef.current);
      progressRef.current = null;
    }
    
    const playCurrentSlide = async () => {
      const slide = DEMO_SLIDES[currentSlide];
      const audioData = audioCache[slide.id];
      
      if (audioData && audioRef.current) {
        audioRef.current.src = `data:audio/mp3;base64,${audioData}`;
        audioRef.current.muted = isMuted;
        
        // Progress tracking
        audioRef.current.ontimeupdate = () => {
          if (audioRef.current && audioRef.current.duration) {
            setSlideProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
          }
        };
        
        audioRef.current.onended = () => {
          setSlideProgress(100);
          setTimeout(() => {
            if (currentSlide < DEMO_SLIDES.length - 1) {
              setCurrentSlide(prev => prev + 1);
              setSlideProgress(0);
            } else {
              // Stop narration completely on the last slide - demo finished
              setIsPlaying(false);
              setDemoCompleted(true); // Mark demo as completed to prevent restart
              setSlideProgress(100);
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current.src = '';
                audioRef.current.onended = null;
                audioRef.current.ontimeupdate = null;
              }
            }
          }, 500);
        };
        
        try {
          await audioRef.current.play();
        } catch (err) {
          console.log('Audio play failed, using timer fallback');
          // Fallback: advance after 8 seconds if audio fails
          setTimeout(() => {
            if (currentSlide < DEMO_SLIDES.length - 1) {
              setCurrentSlide(prev => prev + 1);
            } else {
              // Stop narration completely on the last slide
              setIsPlaying(false);
              setDemoCompleted(true); // Mark demo as completed
              setSlideProgress(100);
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current.src = '';
              }
            }
          }, 8000);
        }
      } else {
        // No audio available, use timer
        let progress = 0;
        progressRef.current = setInterval(() => {
          progress += 1.25;
          setSlideProgress(Math.min(progress, 100));
          if (progress >= 100) {
            clearInterval(progressRef.current);
            progressRef.current = null;
            setTimeout(() => {
              if (currentSlide < DEMO_SLIDES.length - 1) {
                setCurrentSlide(prev => prev + 1);
                setSlideProgress(0);
              } else {
                // Stop narration completely on the last slide - demo finished
                setIsPlaying(false);
                setDemoCompleted(true); // Mark demo as completed
                setSlideProgress(100);
                if (audioRef.current) {
                  audioRef.current.pause();
                  audioRef.current.currentTime = 0;
                  audioRef.current.src = '';
                }
              }
            }, 300);
          }
        }, 100);
      }
    };
    
    playCurrentSlide();
    
    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [isPlaying, currentSlide, audioCache, isMuted, showDemo]);

  // Update muted state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const togglePlay = () => {
    if (isPlaying) {
      setIsPlaying(false);
      if (audioRef.current) audioRef.current.pause();
      if (progressRef.current) clearInterval(progressRef.current);
    } else {
      // If demo was completed and user clicks play, reset to allow replay
      if (demoCompleted) {
        setDemoCompleted(false);
      }
      setIsPlaying(true);
    }
  };

  const goToSlide = (index) => {
    if (audioRef.current) audioRef.current.pause();
    if (progressRef.current) clearInterval(progressRef.current);
    setCurrentSlide(index);
    setSlideProgress(0);
    // Reset demoCompleted if user navigates to allow playback
    if (demoCompleted) {
      setDemoCompleted(false);
    }
  };

  const nextSlide = () => {
    if (currentSlide < DEMO_SLIDES.length - 1) {
      goToSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  };

  const handleCloseDemo = () => {
    setShowDemo(false);
    setIsPlaying(false);
    setCurrentSlide(0);
    setSlideProgress(0);
    setAudioReady(false);
    setLoadingProgress(0);
    setDemoCompleted(false); // Reset demo completed state
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
    if (progressRef.current) clearInterval(progressRef.current);
  };

  const startDemo = () => {
    setShowDemo(true);
    setCurrentSlide(0);
    setSlideProgress(0);
    setAudioReady(false);
    setDemoCompleted(false); // Reset demo completed state for new demo
    // Audio will auto-start when ready via useEffect
  };

  const currentSlideData = DEMO_SLIDES[currentSlide];
  const accentColors = {
    blue: 'from-blue-500 to-blue-600',
    amber: 'from-amber-500 to-amber-600',
    orange: 'from-orange-500 to-orange-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    indigo: 'from-indigo-500 to-indigo-600',
    cyan: 'from-cyan-500 to-cyan-600',
    rose: 'from-rose-500 to-rose-600'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50">
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <MassMasterLogo className="w-8 h-8 sm:w-10 sm:h-10" />
            <span className="text-base sm:text-xl font-bold text-white hidden xs:inline">Mass Master Pro<sup className="text-xs">™</sup></span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Button 
              variant="ghost" 
              className="text-slate-300 hover:text-white hover:bg-slate-800 px-2 sm:px-4 text-sm sm:text-base"
              onClick={startDemo}
              data-testid="watch-demo-btn"
            >
              <Play className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Watch Demo</span>
            </Button>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-blue-600 hover:bg-blue-700 px-3 sm:px-4 text-sm sm:text-base"
              data-testid="sign-in-btn"
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-12 sm:py-16 md:py-28">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 sm:mb-6 bg-blue-600/20 text-blue-400 border-blue-500/30 px-3 sm:px-4 py-1 text-xs sm:text-sm">
            Construction Estimating Software
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight tracking-tight">
            Professional Estimates,<br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Simplified</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-6 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2">
            Mass Master Pro™ helps construction professionals create accurate material estimates 
            and bid documents. Built by contractors, for contractors.
          </p>
          <div className="flex flex-col gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <Button 
              size="lg" 
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-base sm:text-lg px-6 sm:px-8 h-12 sm:h-14 shadow-lg shadow-blue-600/25 w-full sm:w-auto"
              data-testid="start-estimating-btn"
            >
              Start Estimating <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={startDemo}
              className="border-slate-600 text-white hover:bg-slate-800 hover:border-slate-500 text-base sm:text-lg px-6 sm:px-8 h-12 sm:h-14 w-full sm:w-auto"
              data-testid="watch-demo-hero-btn"
            >
              <Play className="mr-2 w-5 h-5 fill-current" /> Watch Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 py-10 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">Eight Specialized Calculators</h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base md:text-lg px-2">
            Each section is designed for specific construction tasks with relevant tools and materials
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {FEATURE_TABS.map((tab, index) => {
            const TabIcon = tab.icon;
            return (
              <Card key={index} className="bg-slate-800/30 border-slate-700/50 hover:bg-slate-800/50 hover:border-slate-600 transition-all duration-300 cursor-pointer group" onClick={() => navigate('/login')}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-3 sm:mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-600/20 to-blue-600/10 flex items-center justify-center group-hover:from-blue-600/30 group-hover:to-blue-600/20 transition-all duration-300">
                      <TabIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-white">{tab.name}</h3>
                  </div>
                  <p className="text-slate-400 text-xs sm:text-sm mb-3 sm:mb-4">{tab.description}</p>
                  <ul className="space-y-1.5 sm:space-y-2">
                    {tab.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs sm:text-sm text-slate-300">
                        <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-10 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          <div className="text-center p-5 sm:p-8 rounded-2xl bg-slate-800/20 border border-slate-700/30">
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-blue-600/20 to-blue-600/10 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">Quick Setup</h3>
            <p className="text-slate-400 text-sm sm:text-base">
              No complicated installation. Access your estimating tools immediately from any web browser.
            </p>
          </div>
          <div className="text-center p-5 sm:p-8 rounded-2xl bg-slate-800/20 border border-slate-700/30">
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-green-600/20 to-green-600/10 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-green-400" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">Custom Materials</h3>
            <p className="text-slate-400 text-sm sm:text-base">
              Add your own materials and costs to any calculator. Your custom data stays saved locally.
            </p>
          </div>
          <div className="text-center p-5 sm:p-8 rounded-2xl bg-slate-800/20 border border-slate-700/30 sm:col-span-2 md:col-span-1">
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-purple-600/20 to-purple-600/10 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">Professional Output</h3>
            <p className="text-slate-400 text-sm sm:text-base">
              Generate bid documents that look professional. Export to PDF, Word, or send via email.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-10 sm:py-16">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50"></div>
          <div className="relative">
            <MassMasterLogo className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-5 sm:mb-8" />
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
              Ready to Streamline Your Estimates?
            </h2>
            <p className="text-blue-100 mb-6 sm:mb-10 max-w-xl mx-auto text-sm sm:text-base md:text-lg px-2">
              Join contractors who use Mass Master Pro to create professional estimates efficiently.
            </p>
            <Button 
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-white text-blue-600 hover:bg-slate-100 text-base sm:text-lg px-6 sm:px-10 h-12 sm:h-14 font-semibold shadow-lg w-full sm:w-auto"
            >
              Get Started Free <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 sm:py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-3 sm:gap-4 text-center">
            <div className="flex items-center gap-2 sm:gap-3">
              <MassMasterLogo className="w-6 h-6 sm:w-8 sm:h-8" />
              <span className="text-slate-400 font-medium text-sm sm:text-base">Mass Master Pro<sup className="text-xs">™</sup></span>
            </div>
            <p className="text-slate-500 text-xs sm:text-sm">
              © {new Date().getFullYear()} Mass Master Pro™. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Audio Element */}
      <audio ref={audioRef} preload="auto" />

      {/* Professional Demo Modal */}
      {showDemo && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-2 sm:p-4">
          <div className="w-full max-w-5xl">
            {/* Demo Header */}
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <MassMasterLogo className="w-6 h-6 sm:w-8 sm:h-8" />
                <span className="text-white font-semibold text-sm sm:text-lg">Mass Master Pro™ Demo</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleCloseDemo}
                className="text-slate-400 hover:text-white hover:bg-slate-800 p-2"
                data-testid="close-demo-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Loading Screen - Show while audio is being preloaded */}
            {audioLoading && (
              <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl min-h-[350px] sm:min-h-[500px] flex flex-col items-center justify-center p-4">
                <MassMasterLogo className="w-16 h-16 sm:w-24 sm:h-24 mb-4 sm:mb-8 animate-pulse" />
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-4">Preparing Demo</h2>
                <p className="text-slate-400 mb-4 sm:mb-6 text-sm sm:text-base">Loading audio narration...</p>
                <div className="w-48 sm:w-64 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
                    style={{ width: `${loadingProgress}%` }}
                  />
                </div>
                <p className="text-slate-500 text-xs sm:text-sm mt-2 sm:mt-3">{loadingProgress}% - Loading slide {Math.ceil(loadingProgress / (100 / DEMO_SLIDES.length))} of {DEMO_SLIDES.length}</p>
              </div>
            )}

            {/* Main Demo Content - Only show when audio is ready */}
            {!audioLoading && (
              <div className={`relative bg-gradient-to-br ${currentSlideData.bgGradient} rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl`}>
              {/* Slide Content */}
              <div className="min-h-[350px] sm:min-h-[500px] flex flex-col items-center justify-center p-4 sm:p-8 md:p-12 text-center relative">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
                </div>
                
                {/* Content */}
                <div className="relative z-10 max-w-3xl mx-auto">
                  {/* Logo Animation */}
                  <div className={`w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-8 rounded-xl sm:rounded-2xl bg-gradient-to-br ${accentColors[currentSlideData.accentColor] || accentColors.blue} p-0.5 shadow-lg`}>
                    <div className="w-full h-full bg-slate-900/80 rounded-xl sm:rounded-2xl flex items-center justify-center backdrop-blur">
                      <MassMasterLogo className="w-10 h-10 sm:w-14 sm:h-14" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-1 sm:mb-3 tracking-tight">
                    {currentSlideData.title}
                  </h2>
                  <p className="text-sm sm:text-xl md:text-2xl text-white/70 mb-4 sm:mb-10">
                    {currentSlideData.subtitle}
                  </p>
                  
                  {/* Features Grid */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 md:gap-6 max-w-xl mx-auto">
                    {currentSlideData.features.map((feature, i) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div 
                          key={i} 
                          className="flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-4 text-left transition-all hover:bg-white/15"
                        >
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-md sm:rounded-lg bg-gradient-to-br ${accentColors[currentSlideData.accentColor] || accentColors.blue} flex items-center justify-center shrink-0`}>
                            <FeatureIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          </div>
                          <span className="text-white font-medium text-xs sm:text-sm md:text-base">{feature.text}</span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* CTA on final slide */}
                  {currentSlideData.isFinal && (
                    <Button 
                      size="lg"
                      onClick={() => {
                        handleCloseDemo();
                        navigate('/login');
                      }}
                      className="mt-4 sm:mt-10 bg-white text-slate-900 hover:bg-slate-100 text-sm sm:text-lg px-4 sm:px-8 h-10 sm:h-14 font-semibold shadow-lg"
                    >
                      Start Estimating Now <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                    </Button>
                  )}
                </div>

                {/* Navigation Arrows */}
                <button 
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-6 sm:h-6" />
                </button>
                <button 
                  onClick={nextSlide}
                  disabled={currentSlide === DEMO_SLIDES.length - 1}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="h-1 bg-black/20">
                <div 
                  className={`h-full bg-gradient-to-r ${accentColors[currentSlideData.accentColor] || accentColors.blue} transition-all duration-100`}
                  style={{ width: `${slideProgress}%` }}
                />
              </div>
            </div>
            )}

            {/* Controls Bar - Only show when not loading */}
            {!audioLoading && (
            <div className="mt-2 sm:mt-4 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-3 order-2 sm:order-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={togglePlay}
                  disabled={audioLoading}
                  className="text-white hover:bg-slate-800 h-8 sm:h-10 px-2 sm:px-4"
                  data-testid="play-pause-btn"
                >
                  {audioLoading ? (
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                  )}
                  <span className="ml-1 sm:ml-2 text-xs sm:text-sm">{isPlaying ? 'Pause' : 'Play'}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMuted(!isMuted)}
                  className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 sm:h-10 px-2 sm:px-3"
                >
                  {isMuted ? <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" /> : <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />}
                </Button>
              </div>

              {/* Slide Indicators */}
              <div className="flex items-center gap-1.5 sm:gap-2 order-1 sm:order-2">
                {DEMO_SLIDES.map((slide, i) => (
                  <button
                    key={i}
                    onClick={() => goToSlide(i)}
                    className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                      i === currentSlide 
                        ? `w-4 sm:w-8 bg-gradient-to-r ${accentColors[slide.accentColor]}` 
                        : 'w-1.5 sm:w-2 bg-slate-600 hover:bg-slate-500'
                    }`}
                    title={slide.title}
                    data-testid={`slide-indicator-${i}`}
                  />
                ))}
              </div>

              <Button
                onClick={() => {
                  handleCloseDemo();
                  navigate('/login');
                }}
                className="bg-blue-600 hover:bg-blue-700 h-8 sm:h-10 text-xs sm:text-sm px-3 sm:px-4 order-3"
                data-testid="try-it-now-btn"
              >
                Try It Now <ArrowRight className="ml-1 sm:ml-2 w-3 h-3 sm:w-4 sm:h-4" />
              </Button>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
